import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { isAxiosError } from 'axios';

import { IApiHookResponseError } from '@api/interfaces/apiHookResponseError.interface';
import { IApiHookRequestParams } from '@api/interfaces/apiHookRequestParams.interface';
import { IApiHookProps } from '@api/interfaces/apiHookProps.interface';
import { IApiHook } from '@api/interfaces/apiHook.interface';
import { useRequest } from '@api/useRequest/useRequest';
import { httpClient } from '@okta/axiosInstance';
import api from '@configs/urls/api';

import { IUseGetAssignedApplicationsParams } from './interfaces/useGetAssignedApplicationsParams.interface';

const useGetAssignedApplications = (
  props: IApiHookProps<IUseGetAssignedApplicationsParams>,
): IApiHook<string[], IApiHookResponseError, IUseGetAssignedApplicationsParams, unknown> => {
  const {
    autoRequest = false,
    callbacks = {},
  } = props;
  const {
    setErrorDetails,
    setIsLoading,
    setIsError,
    setData,
  } = callbacks;
  const { t } = useTranslation();
  const { requestStatusState } = useRequest(autoRequest);

  const makeRequestHandler = useCallback(async (
    configs?: IApiHookRequestParams<IUseGetAssignedApplicationsParams, unknown>,
  ): Promise<string[]> => {
    const params = configs?.params || { domain: 'salesslip' };
    setIsLoading?.(true);
    try {
      const response = await httpClient.get<string[]>({
        url: api.getAssignedApplications(),
        config: {
          params,
          baseURL: import.meta.env.REACT_APP_AUTH_API_URL,
        },
      });
      const assignedApplications = response.data;
      setData?.(assignedApplications);
      return assignedApplications;
    } catch (error: unknown) {
      // Don't handle abort errors - they're expected when requests are cancelled
      if (isAxiosError(error) && error.code === 'ERR_CANCELED') {
        throw error;
      }
      if (isAxiosError(error) && error.response) {
        setErrorDetails?.({
          status: error.response.status,
          message: t('errors.common_error', { ns: 'common' }),
        });
        setIsError?.(true);
      }
      throw error;
    } finally {
      setIsLoading?.(false);
    }
  }, [httpClient, t, setErrorDetails, setIsError, setIsLoading, setData]);

  return {
    ...requestStatusState,
    data: null,
    makeRequestHandler,
  };
};

export default useGetAssignedApplications;
